import {
  Button,
  cmdkItemValue,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  PolicyStatus,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components';
import { cn } from '@folio/lib';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import type { EmailThreadLinkedPolicyDto, PolicyBranchesForBrokerEntity } from 'korint-sdk';
import {
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListBrokerageFirmPoliciesQueryOptions,
} from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import { groupBy, uniqBy } from 'lodash';
import { CheckIcon, type LucideIcon, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REVEALED_ON_HOVER } from './threadHelpers';
import { usePolicyLabel } from './ThreadIndicators';
import { Text } from '@folio/components/ui/text';

const RESULT_COUNT = 10;
// Same scope as automatic linking: issued contracts and quotes on an open new-business branch
const VIEW_TYPES = ['live', 'newBusiness'] as const;

type Option = { policy: EmailThreadLinkedPolicyDto; branch?: PolicyBranchesForBrokerEntity };

const GROUPS = ['active', 'quotes', 'terminated'] as const;
const groupOf = (status: EmailThreadLinkedPolicyDto['status']): (typeof GROUPS)[number] => {
  if (status === 'POLICY_STOPPED') return 'terminated';
  if (status === 'POLICY_SIGNED' || status === 'POLICY_STARTED' || status === 'POLICY_SUSPENDED')
    return 'active';
  return 'quotes';
};

// Mounted only while the popover is open, which resets the search on close
function PolicySearch({
  pinnedPolicies,
  participantAddresses,
  excludedPolicies = [],
  onSelect,
  isSelected,
  isDisabled,
}: {
  // Listed first until the broker types
  pinnedPolicies: EmailThreadLinkedPolicyDto[];
  // Searched until the broker types: the search covers customers' email addresses
  participantAddresses: string[];
  excludedPolicies?: EmailThreadLinkedPolicyDto[];
  onSelect: (policy: EmailThreadLinkedPolicyDto) => void;
  isSelected?: (policyId: string) => boolean;
  isDisabled?: (policyId: string) => boolean;
}) {
  const { t } = useTranslation();
  const { title: policyTitle, details: policyDetails } = usePolicyLabel();
  const [search, setSearch] = useState('');
  const typed = useDebouncedValue(search.trim(), 300);
  const queryStrings = typed
    ? [typed]
    : participantAddresses.length > 0
      ? participantAddresses
      : [''];
  const { data: { brokerageFirmId } = {} } = useQuery(
    legacyPermissionsControllerAtMeQueryOptions(),
  );
  const results = useQueries({
    queries: queryStrings.flatMap(queryString =>
      VIEW_TYPES.map(viewType => ({
        ...viewSpecificControllerListBrokerageFirmPoliciesQueryOptions(brokerageFirmId ?? '', {
          viewType,
          queryString,
          expandCustomers: true,
          pageInfo: { page: 1, count: RESULT_COUNT },
        }),
        enabled: !!brokerageFirmId,
        placeholderData: keepPreviousData,
      })),
    ),
  });
  const isFetching = results.some(({ isFetching }) => isFetching);
  const searched = results.flatMap(({ data }) =>
    (data?.data ?? []).map(
      ({ policyId, branches: [branch] }): Option => ({
        branch,
        policy: {
          id: policyId,
          externalId: branch?.policy.externalId ?? null,
          productId: branch?.policy.productId ?? null,
          status: branch?.policy.status ?? null,
          customerName: branch?.customerName ?? null,
          createdAt: branch?.policy.createdAt ?? null,
        },
      }),
    ),
  );
  const branchOf = (policyId: string) =>
    searched.find(({ policy }) => policy.id === policyId)?.branch;
  const pinned = typed ? [] : pinnedPolicies;
  const options = uniqBy(
    [...pinned.map(policy => ({ policy, branch: branchOf(policy.id) })), ...searched],
    ({ policy }) => policy.id,
  ).filter(({ policy }) => !excludedPolicies.some(({ id }) => id === policy.id));
  const optionsByGroup = groupBy(options, ({ policy, branch }) =>
    groupOf(branch?.policy.status ?? policy.status),
  );

  return (
    <>
      <CommandInput
        placeholder={t('generalInbox.policyLinks.search')}
        aria-label={t('generalInbox.policyLinks.search')}
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        {/* Not CommandEmpty: cmdk 0.2 never renders it with shouldFilter={false} */}
        {!isFetching && options.length === 0 && (
          <Text size="sm" className="py-6 text-center">
            {t('generalInbox.policyLinks.empty')}
          </Text>
        )}
        {GROUPS.filter(group => optionsByGroup[group]).map(group => (
          <CommandGroup key={group} heading={t(`generalInbox.policyLinks.groups.${group}`)}>
            {optionsByGroup[group].map(({ policy, branch }) => (
              <CommandItem
                key={policy.id}
                value={cmdkItemValue(policy.id)}
                onSelect={() => onSelect(policy)}
                disabled={isDisabled?.(policy.id)}
                data-testid="general-inbox-policy-option"
              >
                {isSelected && (
                  <CheckIcon
                    className={cn(
                      'size-4 shrink-0',
                      isSelected(policy.id) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                )}
                <span className="flex min-w-0 flex-1 flex-col">
                  <Text inline truncate>
                    {policyTitle(policy)}
                  </Text>
                  {policyDetails(policy) && (
                    <Text inline truncate size="xs" tone="muted">
                      {policyDetails(policy)}
                    </Text>
                  )}
                </span>
                {branch ? (
                  <PolicyStatus policy={branch.policy} workingBranch={branch.branch} />
                ) : (
                  policy.status && (
                    <PolicyStatus
                      policy={{
                        status: policy.status,
                        productId: policy.productId ?? undefined,
                      }}
                    />
                  )
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </>
  );
}

export function PolicyPicker({
  linkedPolicies,
  suggestedPolicies,
  participantAddresses,
  onAdd,
  disabled,
}: {
  linkedPolicies: EmailThreadLinkedPolicyDto[];
  // Already linked to the conversation's other emails
  suggestedPolicies: EmailThreadLinkedPolicyDto[];
  participantAddresses: string[];
  onAdd: (policy: EmailThreadLinkedPolicyDto) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          className="relative z-10 h-6 gap-1 px-2 text-xs text-muted-foreground"
          leftIcon={<Plus className="size-3.5" />}
          disabled={disabled}
          data-testid="general-inbox-add-policy"
        >
          {t('generalInbox.policyLinks.add')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={8} className="w-96 p-0">
        <Command shouldFilter={false}>
          <PolicySearch
            pinnedPolicies={suggestedPolicies}
            participantAddresses={participantAddresses}
            excludedPolicies={linkedPolicies}
            onSelect={policy => {
              onAdd(policy);
              setOpen(false);
            }}
          />
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function MultiplePolicyPicker({
  label,
  icon: Icon,
  suggestedPolicies,
  participantAddresses,
  onSubmit,
  className,
  testId,
  disabled,
  maxSelected,
}: {
  label: string;
  icon: LucideIcon;
  suggestedPolicies: EmailThreadLinkedPolicyDto[];
  participantAddresses: string[];
  onSubmit: (policies: EmailThreadLinkedPolicyDto[]) => void;
  className?: string;
  testId: string;
  disabled?: boolean;
  maxSelected: number;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<EmailThreadLinkedPolicyDto[]>([]);
  const isSelected = (policyId: string) => selected.some(({ id }) => id === policyId);
  const isFull = selected.length >= maxSelected;

  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setSelected([]);
  };
  const submit = () => {
    onSubmit(selected);
    changeOpen(false);
  };
  const toggle = (policy: EmailThreadLinkedPolicyDto) =>
    setSelected(previous =>
      isSelected(policy.id) ? previous.filter(({ id }) => id !== policy.id) : [...previous, policy],
    );

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'relative z-10 size-6',
                REVEALED_ON_HOVER,
                'data-[state=open]:opacity-100',
                className,
              )}
              aria-label={label}
              data-testid={testId}
              disabled={disabled}
            >
              <Icon className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" collisionPadding={8} className="w-96 p-0">
        <Command shouldFilter={false}>
          <PolicySearch
            pinnedPolicies={[...selected, ...suggestedPolicies]}
            participantAddresses={participantAddresses}
            onSelect={toggle}
            isSelected={isSelected}
            isDisabled={policyId => isFull && !isSelected(policyId)}
          />
          <div className="border-t p-2">
            {isFull && (
              <Text size="xs" tone="muted" className="pb-2 text-center">
                {t('generalInbox.policyLinks.limit', { count: maxSelected })}
              </Text>
            )}
            <Button size="sm" className="w-full" disabled={selected.length === 0} onClick={submit}>
              {t('generalInbox.policyLinks.save')}
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
